import { ChevronRightIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  HStack,
  Icon,
  Skeleton,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';

import {
  type CompanyPeekData,
  CompanyPeekTooltip,
} from '../company-details/CompanyPeekTooltip';
import type { CompanyMetrics } from '../hooks/useCompetitorComposition';
import { useCurrencyFormatter } from '../hooks/useCurrencyFormatter';
import type { RiskLevel } from '../hooks/useEmployerHealth';
import type { RoleLevel } from '../hooks/useResolveProfileIds';
import { RISK_BG, RISK_BORDER, RISK_COLORS } from '../utils/colors';
import { formatDateRange } from '../utils/formatDateRange';

export interface CareerMoveStep {
  rcid: number;
  companyName: string;
  title: string;
  startDate: string;
  endDate: string;
  salary: number;
  stabilityScore: number | null;
  risk: RiskLevel | null;
  sentimentOverall: number | null;
}

interface CareerMovesInsightProps {
  steps: CareerMoveStep[];
  isLoading: boolean;
  metrics?: CompanyMetrics[];
  userSalary?: number;
  userRoleLevel?: RoleLevel | null;
}

/** Check if a step falls within the last N years from today */
const isWithinYears = (step: CareerMoveStep, years: number): boolean => {
  const cutoff = new Date();
  cutoff.setFullYear(cutoff.getFullYear() - years);
  const cutoffStr = cutoff.toISOString().slice(0, 10);

  // If the step has an end date, use it; otherwise it's current (always within range)
  const endDate = step.endDate || new Date().toISOString().slice(0, 10);
  return endDate >= cutoffStr;
};

const RECENT_YEARS = 5;

const getVerdict = (
  steps: CareerMoveStep[]
): { text: string; color: string } => {
  const scored = steps.filter((s) => s.stabilityScore !== null);
  if (scored.length < 2) {
    return {
      text: 'Not enough data to assess career trajectory.',
      color: 'gray.500',
    };
  }

  // Compare oldest to most recent (steps are in most-recent-first order)
  const mostRecent = scored[0];
  const oldest = scored[scored.length - 1];
  if (!mostRecent || !oldest) {
    return {
      text: 'Not enough data to assess career trajectory.',
      color: 'gray.500',
    };
  }

  const delta = (mostRecent.stabilityScore ?? 0) - (oldest.stabilityScore ?? 0);

  if (delta > 10) {
    return {
      text: 'Your career moves have trended toward more stable employers.',
      color: 'green.600',
    };
  }
  if (delta < -10) {
    return {
      text: 'Your most recent employer is less stable than your earlier ones \u2014 consider the health metrics below.',
      color: 'orange.600',
    };
  }
  return {
    text: 'Your employers have had similar stability levels across your career.',
    color: 'gray.600',
  };
};

export const CareerMovesInsight = ({
  steps,
  isLoading,
  metrics,
  userSalary = 0,
  userRoleLevel,
}: CareerMovesInsightProps) => {
  const metricsMap = useMemo(
    () => new Map((metrics ?? []).map((m) => [m.companyId, m])),
    [metrics]
  );
  const salaryFormatter = useCurrencyFormatter();
  const [showAll, setShowAll] = useState(false);

  // Steps come in work_history order (most recent first from useWorkHistoryCompanies).
  // We display most recent first (left to right).
  const recentSteps = useMemo(
    () => steps.filter((s) => isWithinYears(s, RECENT_YEARS)),
    [steps]
  );

  const olderSteps = useMemo(
    () => steps.filter((s) => !isWithinYears(s, RECENT_YEARS)),
    [steps]
  );

  const displaySteps = showAll ? steps : recentSteps;
  const verdict = getVerdict(displaySteps);

  if (isLoading) {
    return <Skeleton height="100px" borderRadius="md" />;
  }

  if (displaySteps.length < 2 && !showAll && olderSteps.length > 0) {
    // Not enough recent steps but older ones exist — show all
    return (
      <Box
        bg="gray.50"
        p={4}
        borderRadius="md"
        border="1px solid"
        borderColor="gray.200"
      >
        <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={2}>
          Career Moves Timeline
        </Text>
        <Text fontSize="xs" color="gray.500" mb={2}>
          No roles in the last {RECENT_YEARS} years to compare.
        </Text>
        <Button
          size="xs"
          variant="link"
          colorScheme="blue"
          onClick={() => setShowAll(true)}
        >
          Show full career history ({steps.length} roles)
        </Button>
      </Box>
    );
  }

  if (displaySteps.length < 2) return null;

  return (
    <Box
      bg="gray.50"
      p={4}
      borderRadius="md"
      border="1px solid"
      borderColor="gray.200"
    >
      <HStack justify="space-between" mb={3}>
        <Text fontSize="xs" fontWeight="semibold" color="gray.600">
          Career Moves Timeline
          {!showAll && olderSteps.length > 0 && (
            <Text as="span" color="gray.400" fontWeight="normal">
              {' '}
              (last {RECENT_YEARS} years)
            </Text>
          )}
        </Text>
        {showAll && olderSteps.length > 0 && (
          <Button
            size="xs"
            variant="link"
            colorScheme="blue"
            onClick={() => setShowAll(false)}
          >
            Show recent only
          </Button>
        )}
      </HStack>

      <HStack spacing={0} align="flex-start" overflowX="auto" pb={2}>
        {displaySteps.map((step, idx) => {
          const risk = step.risk ?? 'medium';
          const hasScore = step.stabilityScore !== null;

          return (
            <HStack key={step.rcid} spacing={0} align="center">
              {/* Step card */}
              <VStack
                spacing={1}
                align="center"
                minW="120px"
                maxW="160px"
                px={2}
              >
                {/* Stability bubble */}
                <Box
                  w="40px"
                  h="40px"
                  borderRadius="full"
                  bg={hasScore ? RISK_BG[risk] : 'gray.100'}
                  border="2px solid"
                  borderColor={hasScore ? RISK_BORDER[risk] : 'gray.300'}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Text
                    fontSize="xs"
                    fontWeight="bold"
                    color={hasScore ? RISK_COLORS[risk] : 'gray.400'}
                  >
                    {hasScore ? step.stabilityScore : '\u2014'}
                  </Text>
                </Box>

                <CompanyPeekTooltip
                  data={
                    {
                      rcid: step.rcid,
                      name: step.companyName,
                      headcount: metricsMap.get(step.rcid)?.headcount ?? null,
                      salary:
                        metricsMap.get(step.rcid)?.salary ??
                        (step.salary > 0 ? step.salary : null),
                      growthRate: metricsMap.get(step.rcid)?.growthRate ?? null,
                      stabilityScore: step.stabilityScore,
                      risk: step.risk,
                      sentimentOverall: step.sentimentOverall,
                      linkedInUrl: null,
                    } satisfies CompanyPeekData
                  }
                  userSalary={userSalary}
                  userRoleLevel={userRoleLevel ?? null}
                >
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    color="gray.700"
                    textAlign="center"
                    noOfLines={1}
                    cursor="pointer"
                    _hover={{ textDecoration: 'underline' }}
                  >
                    {step.companyName}
                  </Text>
                </CompanyPeekTooltip>
                <Text
                  fontSize="2xs"
                  color="gray.400"
                  textAlign="center"
                  noOfLines={1}
                >
                  {step.title}
                </Text>
                <Text fontSize="2xs" color="gray.400">
                  {formatDateRange(step.startDate, step.endDate)}
                </Text>
                {step.salary > 0 && (
                  <Text fontSize="xs" fontWeight="medium" color="gray.600">
                    {salaryFormatter(step.salary)}
                  </Text>
                )}
              </VStack>

              {/* Arrow between steps */}
              {idx < displaySteps.length - 1 && (
                <Box px={1}>
                  <Icon as={ChevronRightIcon} boxSize={5} color="gray.400" />
                </Box>
              )}
            </HStack>
          );
        })}
      </HStack>

      <Text fontSize="xs" color={verdict.color} mt={2} fontWeight="medium">
        {verdict.text}
      </Text>

      {!showAll && olderSteps.length > 0 && (
        <Button
          size="xs"
          variant="link"
          colorScheme="blue"
          mt={2}
          onClick={() => setShowAll(true)}
        >
          Show {olderSteps.length} older role{olderSteps.length > 1 ? 's' : ''}
        </Button>
      )}
    </Box>
  );
};
